/**
 * legalAppsCard.ts — the legal apps card's data, and the card shown before its rows arrive (legal apps delivery 3;
 * Miles, 2026-09-28, ruling 5: "a Legal section on the Integrations page, plus a chat tile that opens the same
 * flow"; docs/PLAN-legal-apps-20260928.md §C).
 *
 * `show_legal_apps` is the primary's call (functions/src/legalApps/legalAppsTile.ts): its signal names the catalog
 * keys the card shows. The chat posts ONE data-only message with this module's payload, and the transcript draws
 * the rows — each app's live state and the chat's own connect or sign-in button — from the `legal-apps` download
 * (LegalAppsTile.tsx), the one the Integrations page's legal apps view comes in. The catalog is 63 KB and never
 * rides the chat engine.
 *
 * This is the part the engine keeps: the payload's kind, the check that reads a payload back, and a small card
 * that stands in until the rows arrive, if their download fails, or where the html is all a message has (the
 * byte-budget rebuild in connectTileRehydrate, which runs inside the chat reducer — so this imports the card kit
 * and nothing that touches firebase/auth). It links to the Integrations page's legal apps view.
 *
 * D9: STATE copy (the card's title and its link), never a sentence in Prest0n's voice: the reply beside the card
 * is the primary's.
 */
import { escapeHtml, renderCardShell } from './actionCardKit';
import { LEGAL_APPS_PAGE_HREF } from '../../composio/panel/legalAppsLink';

/** payload.kind for the card's data-only message. */
export const LEGAL_APPS_CARD_KIND = 'prest0n.legal_apps';

/** sourceTag on the posted message (D9 inventory: config/code-authored-copy.d). */
export const LEGAL_APPS_CARD_SOURCE_TAG = 'legal_apps_card';

/** The server's signal kind (functions/src/legalApps/legalAppsTile.ts LEGAL_APPS_SIGNAL_KIND), pinned equal. */
export const LEGAL_APPS_SIGNAL_KIND = 'legal_apps';

/** The most apps one card carries (legalAppsTile.ts LEGAL_APPS_CARD_MAX_APPS), pinned equal. */
export const LEGAL_APPS_CARD_MAX_APPS = 24;

/** The catalog's groups (config/legal-apps.json), pinned equal to the generated catalog by legalAppsCard.test.ts. */
export const LEGAL_APPS_CARD_GROUPS = [
  'practice',
  'research',
  'courts',
  'immigration',
  'documents',
  'billing',
  'phones',
  'mail',
  'intake',
  'people',
  'desktop',
] as const;
export type LegalAppsCardGroup = (typeof LEGAL_APPS_CARD_GROUPS)[number];

/** The in-chat link to the Integrations page's legal apps view: one leaf, read by the stand-in and the rows alike. */
export { LEGAL_APPS_PAGE_HREF };

const KEY_RE = /^[a-z0-9_]{1,40}$/;
const GROUP_SET: ReadonlySet<string> = new Set(LEGAL_APPS_CARD_GROUPS);

export type LegalAppsCardLang = 'en' | 'es';

export interface LegalAppsCardData {
  /** The catalog keys the card shows, in the order the primary's call produced them. */
  apps: string[];
  /** The groups the primary asked for (may be empty: it named apps). */
  groups: LegalAppsCardGroup[];
  lang: LegalAppsCardLang;
}

// copy: legal_apps_card
export const LEGAL_APPS_CARD_COPY = {
  title: { en: 'legal apps', es: 'aplicaciones jurídicas' },
  count: {
    en: (n: number) => (n === 1 ? '1 app, with how it connects today and its button' : `${n} apps, each with how it connects today and its button`),
    es: (n: number) => (n === 1 ? '1 aplicación, con cómo se conecta hoy y su botón' : `${n} aplicaciones, cada una con cómo se conecta hoy y su botón`),
  },
  open: { en: 'open the legal apps', es: 'abrir las aplicaciones jurídicas' },
} as const;

function keysOf(raw: unknown, max: number, allowed?: ReadonlySet<string>): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== 'string' || !KEY_RE.test(v)) return null;
    if (allowed && !allowed.has(v)) return null;
    if (!out.includes(v)) out.push(v);
  }
  return out.length > max ? null : out;
}

/**
 * The card's data from a typed signal (live stream or task lane), or null when the signal is not the card's or
 * names nothing a card can show. A key of any other shape drops the WHOLE signal: a key list the server did not
 * write is never repaired into one.
 */
export function legalAppsCardDataOfSignal(
  signal: { kind?: unknown; legalApps?: unknown; groups?: unknown } | null | undefined,
  lang: LegalAppsCardLang,
): LegalAppsCardData | null {
  if (!signal || signal.kind !== LEGAL_APPS_SIGNAL_KIND) return null;
  const apps = keysOf(signal.legalApps, LEGAL_APPS_CARD_MAX_APPS);
  const groups = signal.groups === undefined ? [] : keysOf(signal.groups, 3, GROUP_SET);
  if (!apps || !groups || (apps.length === 0 && groups.length === 0)) return null;
  return { apps, groups: groups as LegalAppsCardGroup[], lang: lang === 'es' ? 'es' : 'en' };
}

/** Parse a persisted payload into card data — null when it is not the card's, or names nothing. */
export function legalAppsCardDataOf(
  payload: { kind?: string; data?: Record<string, unknown> } | null | undefined,
): LegalAppsCardData | null {
  if (!payload || payload.kind !== LEGAL_APPS_CARD_KIND) return null;
  const d = payload.data && typeof payload.data === 'object' ? payload.data : {};
  return legalAppsCardDataOfSignal(
    { kind: LEGAL_APPS_SIGNAL_KIND, legalApps: d.apps, groups: d.groups },
    d.lang === 'es' ? 'es' : 'en',
  );
}

/**
 * The card before its rows: its title, how many apps it carries, and a link to the Integrations page's legal
 * apps view. The rows replace it the moment their download arrives (legalAppsTileSlot.tsx).
 */
export function buildLegalAppsCardHtml(data: LegalAppsCardData): string {
  const lang = data.lang === 'es' ? 'es' : 'en';
  const copy = LEGAL_APPS_CARD_COPY;
  const count = data.apps.length;
  const pill = `<a class="p0ac-btn p0ac-btn--md p0ac-btn--primary" href="${escapeHtml(LEGAL_APPS_PAGE_HREF)}">${escapeHtml(copy.open[lang])}</a>`;
  return renderCardShell({
    rootAttrs: `data-legal-apps-card="${count}"`,
    maxWidth: 560,
    title: copy.title[lang],
    ...(count > 0 ? { noteHtml: `<div class="p0ac-detail">${escapeHtml(copy.count[lang](count))}</div>` } : {}),
    bodyHtml: `<div class="p0ac-actions">${pill}</div>`,
  });
}
